import { Alert, Box, Button, Collapse, Icon, Text } from '@ksyos/design-system';
import { useNavigate } from '@ksyos/router';
import { lazy, Suspense, useEffect, useState } from 'react';
import type { IPatient } from '@common/interfaces/patient';
import { Icon as CommonIcon, HeaderDivider, IconColor, InputCheckbox } from '@frontend/components/elements/common';
import { PermissionModal } from '@frontend/components/elements/modals/PermissionModal';
import { PatientContactAddEdit } from '@frontend/components/elements/PatientContactAddEdit';
import { fetchJson } from '@frontend/store/fetch';
import { getSelectedLanguage } from '@frontend/translate';
import type { ICustomControlRequestAddEditProps } from '../../interfaces';
import { ExtendPermission } from '../../utils/v1/ExtendPermission';
import { PatientPermissionExtraDetails } from './PatientPermissionExtraDetails';
import { PatientQuestionnaireExtraDetails } from './PatientQuestionnaireExtraDetails';

interface IRequestPatientPermissionProps extends ICustomControlRequestAddEditProps {
    onAccept: (props: { patientPermission: boolean; patientQuestionnaireEmail: boolean }) => Promise<void>;
    patient: IPatient;
    serviceId: string;
}

interface IRequestPatientPermissionState {
    extraDetails: boolean;
    hasContractedCare: boolean | null;
    isEditPatientModalOpen: boolean;
    isInsured: boolean | null;
    isOpen: boolean;
    patientPermission: boolean;
    patientQuestionnaireEmail: boolean;
}

const EditPatientModal = lazy(() =>
    import('@frontend/components/pages/patients/EditPatientModal').then((module) => ({
        default: module.EditPatientModal,
    }))
);

export function RequestPatientPermission(props: IRequestPatientPermissionProps) {
    const navigate = useNavigate();

    const [state, setState] = useState<IRequestPatientPermissionState>({
        extraDetails: false,
        hasContractedCare: null,
        isEditPatientModalOpen: false,
        isInsured: null,
        isOpen: false,
        patientPermission: false,
        patientQuestionnaireEmail: false,
    });

    useEffect(() => {
        fetchPatientInsuranceData();
    }, []);

    if (state.hasContractedCare === null) {
        return;
    }

    return (
        <>
            <PermissionModal
                actionButtons={[
                    <Button
                        key="go-back-button"
                        onClick={() => navigate(props.patient.id ? `/patients/${props.patient.id}` : '/patients')}
                        variant="outline"
                    >
                        {getSelectedLanguage().common.goBack}
                    </Button>,
                    <Button
                        className="automation-button-submit"
                        isDisabled={!state.patientPermission || !props.patient.identifierSocialSecurity}
                        key="continue-button"
                        onClick={onContinue}
                        variant="solid"
                    >
                        {getSelectedLanguage().common.proceed}
                    </Button>,
                ]}
                elementOnTop={renderContractedCareMessage()}
                visible={state.isOpen}
            >
                <div className="mt-4">
                    {!props.patient.identifierSocialSecurity && (
                        <Alert appearance="warning">
                            We hebben geen BSN voor deze patiënt en de patient kan daarom niet verwezen worden.
                            <br />
                            <Alert.Link
                                onClick={() => setState((prev) => ({ ...prev, isEditPatientModalOpen: true }))}
                                variant="button"
                            >
                                <u>Klik hier</u>
                            </Alert.Link>{' '}
                            om het BSN toe te voegen voor deze patient.
                        </Alert>
                    )}

                    {state.hasContractedCare === false && (
                        <PatientContactAddEdit
                            {...props}
                            addressMandatory
                            alertMessage={
                                !state.isInsured
                                    ? 'Vul de contactgegevens van de patiënt in, deze zijn van belang voor de facturatie'
                                    : 'Controleer de ingevulde contactgegevens van de patiënt, deze zijn van belang voor de facturatie'
                            }
                            showAddress
                        />
                    )}
                    <HeaderDivider text={getSelectedLanguage().osasPermission.referralPermission} />
                    <InputCheckbox
                        checked={state.patientPermission}
                        isRequired
                        onChange={(value) => {
                            setState((prev) => ({ ...prev, patientPermission: value }));
                        }}
                        text={getSelectedLanguage().osasPermission.patientPermission}
                    />
                    <Collapse isOpen={state.extraDetails}>
                        <PatientPermissionExtraDetails />
                    </Collapse>

                    <InputCheckbox
                        checked={state.patientQuestionnaireEmail}
                        onChange={(value) => {
                            setState((prev) => ({ ...prev, patientQuestionnaireEmail: value }));
                        }}
                        text={getSelectedLanguage().osasPermission.patientQuestionnaire}
                    />
                    <Collapse isOpen={state.extraDetails}>
                        <PatientQuestionnaireExtraDetails />
                    </Collapse>

                    <ExtendPermission
                        onClick={() => setState((prev) => ({ ...prev, extraDetails: !prev.extraDetails }))}
                        show={state.extraDetails}
                    />
                </div>
            </PermissionModal>
            <Suspense>
                <EditPatientModal
                    isOpen={state.isEditPatientModalOpen}
                    onClose={() => setState((prev) => ({ ...prev, isEditPatientModalOpen: false }))}
                    onSavePatientDetails={async () => {
                        if (props.reloadPatient) {
                            await props.reloadPatient();
                        }
                    }}
                    params={{ id: props.patient.id }}
                />
            </Suspense>
        </>
    );

    async function fetchPatientInsuranceData() {
        const url = `/api/patients/${props.patient.id}/hasContractedCare/${props.serviceId}`;

        const result = await fetchJson(url, 'GET');

        setState((prev) => ({
            ...prev,
            hasContractedCare: result.json.hasContractedCare,
            isInsured: result.json.isInsured,
            isOpen: true,
        }));
    }

    async function onContinue() {
        if (state.hasContractedCare === false) {
            const contactgegevensKeys = Object.keys(props.request.customControlData.contactgegevens_patient);

            for (const contactgegevensKey of contactgegevensKeys) {
                await props.validateOneCustomControl(`contactgegevens_patient.${contactgegevensKey}`);
            }
        }

        if (!props.errors.size) {
            setState((prev) => ({ ...prev, isOpen: false }));

            props.onAccept({
                patientPermission: state.patientPermission,
                patientQuestionnaireEmail: state.patientQuestionnaireEmail,
            });
        }
    }

    function renderContractedCareMessage() {
        let header;
        let content;

        if (!state.isInsured) {
            header = (
                <Box
                    alignItems="center"
                    display="flex"
                    mb={2}
                >
                    <Icon
                        color="warning"
                        icon="warning"
                        sx={{ flexShrink: 0 }}
                    />
                    <Text
                        color="warning"
                        fontWeight="bold"
                        ml={2}
                        size="lg"
                    >
                        Let op: deze patiënt is niet verzekerd in Nederland.
                    </Text>
                </Box>
            );
            content = (
                <Text
                    color="warning"
                    size="sm"
                >
                    Dit betekent dat het het onderzoek en eventuele behandeling in kosten wordt gebracht bij de patiënt
                    of bij een buitenlandse verzekering moet worden gedeclareerd door de patiënt.
                </Text>
            );
        } else if (state.hasContractedCare) {
            header = (
                <Text
                    mb={2}
                    size="lg"
                >
                    Deze patiënt ontvangt <strong>gecontracteerde</strong> zorg
                </Text>
            );
            content = (
                <Text size="sm">
                    De zorgverzekering ontvangt de factuur van Ksyos. <br />
                    Deze zorg valt onder het eigen risico. De patiënt betaalt bij Ksyos nooit meer dan het eigen risico.
                </Text>
            );
        } else {
            header = (
                <Text
                    color="warning"
                    fontWeight="bold"
                    mb={2}
                    size="lg"
                >
                    Deze patiënt ontvangt ongecontracteerde zorg
                </Text>
            );
            content = (
                <Text
                    color="warning"
                    size="sm"
                >
                    De <strong>patiënt</strong> ontvangt de factuur van Ksyos. <br />
                    De patiënt zal de factuur bij de zorgverzekeraar moeten declareren.
                    <br />
                    Deze zorg valt onder het eigen risico. De patiënt betaalt bij Ksyos nooit meer dan het eigen risico.
                </Text>
            );
        }

        return (
            <Box
                backgroundColor={!state.hasContractedCare ? 'orange100' : null}
                display="flex"
                justifyContent="center"
            >
                <Box
                    maxWidth={'840px'}
                    width="100%"
                >
                    <Box
                        alignItems="center"
                        display="flex"
                        flexDirection={['column', 'row']}
                        p={4}
                    >
                        <Box
                            flex="0 0 auto"
                            mr={[0, 4]}
                        >
                            <CommonIcon
                                className="costs-icon"
                                color={!state.hasContractedCare ? IconColor.dangerBorder : undefined}
                                icon="costs"
                            />
                        </Box>
                        <Box flex="1">
                            {header}
                            {content}
                        </Box>
                    </Box>
                </Box>
            </Box>
        );
    }
}
